import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};
const emptyStr = (v) => (v === '' || v == null ? null : v);

const enrichPropertyBusinessUnitRows = async (rows, tenantId) => {
  const groups = await apiFetch(
    `conductor/cash-recon/property-groups/by-tenant?tenant_id=${tenantId}`
  ).catch(() => []);
  const groupMap = Object.fromEntries(
    (Array.isArray(groups) ? groups : []).map((g) => [g.propertyGroupId, g.groupName])
  );
  return rows.map((row) => ({
    ...row,
    parentName: groupMap[row.parentBusinessUnitId] || groupMap[row.raw?.propertyGroupId] || '',
  }));
};

const defaultForm = {
  businessUnitCode: '',
  businessUnitName: '',
  parentBusinessUnitId: '',
  status: 'ACTIVE',
};

const mapBusinessUnitRow = (item) => ({
  id: item.propertyId,
  businessUnitCode: item.propertyCode || '',
  businessUnitName: item.propertyName || '',
  parentBusinessUnitId: item.propertyGroupId || '',
  parentName: '',
  status: item.status,
  raw: item,
});

const BusinessUnitMaster = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.ACCOUNTS_PAYABLE);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [codeFocused, setCodeFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [groupFocused, setGroupFocused] = useState(false);

  const focusLabelProps = (hasValue, focused) => ({
    shrink: hasValue || focused,
    sx: (hasValue || focused) ? { bgcolor: 'background.paper', px: 0.5 } : undefined,
  });

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const loadPropertyGroups = useCallback(async () => {
    if (!tenantId) return;
    try {
      const data = await apiFetch(`conductor/cash-recon/property-groups/by-tenant?tenant_id=${tenantId}`);
      setPropertyGroups(Array.isArray(data) ? data : []);
    } catch {
      setPropertyGroups([]);
    }
  }, [tenantId]);

  const fetchBusinessUnits = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
      let mapped = (Array.isArray(data) ? data : []).map(mapBusinessUnitRow);
      mapped = await enrichPropertyBusinessUnitRows(mapped, tenantId);
      setRows(mapped);
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load business units');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    loadPropertyGroups();
    fetchBusinessUnits();
  }, [loadPropertyGroups, fetchBusinessUnits]);

  const columns = useMemo(() => [
    { field: 'businessUnitCode', headerName: 'Code', width: 140, resizable: true },
    { field: 'businessUnitName', headerName: 'Name', width: 220, resizable: true },
    { field: 'parentName', headerName: 'Property Group', width: 180, resizable: true },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
  ], []);

  const buildPayload = () => ({
    tenantId,
    ...(mode === 'edit' && editingId ? { propertyId: editingId } : {}),
    propertyCode: form.businessUnitCode?.trim(),
    propertyName: form.businessUnitName?.trim(),
    propertyGroupId: emptyStr(form.parentBusinessUnitId),
    propertyType: 'HOTEL',
    cashReconEnabled: true,
    status: form.status || 'ACTIVE',
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setCodeFocused(false);
    setNameFocused(false);
    setGroupFocused(false);
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setCodeFocused(false);
    setNameFocused(false);
    setGroupFocused(false);
    setForm({
      businessUnitCode: selectedRow.businessUnitCode || '',
      businessUnitName: selectedRow.businessUnitName || '',
      parentBusinessUnitId: selectedRow.parentBusinessUnitId || '',
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/cash-recon/properties', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId, updatedBy: userId }),
        });
        showSuccess('Business Unit Master saved successfully');
      } else {
        await apiFetch(`conductor/cash-recon/properties/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ ...payload, updatedBy: userId }),
        });
        showSuccess('Business Unit Master updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchBusinessUnits();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/cash-recon/properties/hard/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Record deleted successfully');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      fetchBusinessUnits();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Business Unit Master</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit Business Unit Master' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add Business Unit Master' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete Business Unit Master' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Business Unit Master' : 'Edit Business Unit Master'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              required
              label="Business Unit Code"
              value={form.businessUnitCode}
              onChange={(e) => setForm((p) => ({ ...p, businessUnitCode: e.target.value }))}
              onFocus={() => setCodeFocused(true)}
              onBlur={() => setCodeFocused(false)}
              InputLabelProps={focusLabelProps(Boolean(form.businessUnitCode), codeFocused)}
            />
            <TextField
              fullWidth
              size="small"
              required
              label="Business Unit Name"
              value={form.businessUnitName}
              onChange={(e) => setForm((p) => ({ ...p, businessUnitName: e.target.value }))}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              InputLabelProps={focusLabelProps(Boolean(form.businessUnitName), nameFocused)}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              select
              required
              label="Property Group"
              value={form.parentBusinessUnitId}
              onChange={(e) => setForm((p) => ({ ...p, parentBusinessUnitId: e.target.value }))}
              onFocus={() => setGroupFocused(true)}
              onBlur={() => setGroupFocused(false)}
              InputLabelProps={focusLabelProps(Boolean(form.parentBusinessUnitId), groupFocused)}
              SelectProps={{
                displayEmpty: true,
                MenuProps: harmonateSelectMenuProps,
                renderValue: (selected) => {
                  if (!selected) return '';
                  const group = propertyGroups.find((g) => g.propertyGroupId === selected);
                  return group?.groupName || selected;
                },
              }}
            >
              <MenuItem value="">
                <em>Select property group</em>
              </MenuItem>
              {propertyGroups.map((group) => (
                <MenuItem key={group.propertyGroupId} value={group.propertyGroupId}>{group.groupName}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} InputLabelProps={{ shrink: true, sx: { bgcolor: 'background.paper', px: 0.5 } }} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}><Typography sx={{ color: ht.text }}>Are you sure you want to delete &quot;{rowPendingDelete?.businessUnitName || 'selected'}&quot;?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default BusinessUnitMaster;
